(() => {
  const roots = document.querySelectorAll("[data-nashe-judgeme-api-reviews]");
  if (!roots.length) return;

  const esc = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  }[char]));

  const clean = (value) => String(value ?? "").replace(/\s+/g, " ").trim();

  const decodeHtml = (value) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = String(value ?? "");
    return textarea.value;
  };

  const htmlText = (value) => clean(decodeHtml(String(value ?? "").replace(/<[^>]*>/g, " ")));

  const stars = (rating) => {
    const score = Math.max(0, Math.min(5, Math.round(Number(rating) || 5)));
    return "★★★★★".slice(0, score) + "☆☆☆☆☆".slice(0, 5 - score);
  };

  const dateText = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const firstPicture = (review) => {
    const pictures = Array.isArray(review.pictures_urls) ? review.pictures_urls : [];
    const first = pictures[0];
    if (!first) return "";
    if (typeof first === "string") return first;
    return first.compact || first.thumb || first.small || first.original || first.url || first.href || "";
  };

  const productImage = (review) => review.product_variant_image_url || review.product_pictures_url || "";

  const photoKey = (photo) => {
    if (!photo) return "";
    try {
      const url = new URL(photo, window.location.origin);
      return `${url.hostname}${url.pathname}`.toLowerCase();
    } catch {
      return String(photo).split("?")[0].toLowerCase();
    }
  };

  const largePhotoUrl = (photo) => {
    if (!photo || !String(photo).includes("review-images.judgeme.com")) return photo;
    try {
      const url = new URL(photo, window.location.origin);
      url.searchParams.set("quality", "90");
      url.searchParams.set("width", "900");
      return url.href;
    } catch {
      return photo;
    }
  };

  const isProductReview = (review) => {
    const url = clean(review.product_url);
    return review.product_in_store !== false && url.startsWith("/products/");
  };

  const normalize = (review) => ({
    id: review.uuid || `${review.reviewer_name}-${review.created_at}`,
    author: clean(review.reviewer_name) || "NASHE customer",
    initial: clean(review.reviewer_initial || review.reviewer_name?.[0]) || "N",
    rating: Number(review.rating) || 5,
    body: clean(review.body || review.body_html?.replace(/<[^>]*>/g, "")),
    verified: Boolean(review.verified_buyer),
    date: dateText(review.created_at),
    productTitle: clean(review.product_title) || "NASHE",
    productUrl: clean(review.product_url) || "/collections/all-products",
    productImage: productImage(review),
    photo: firstPicture(review),
  });

  const normalizePhotoReviewNode = (node) => {
    const ratingNode = node.querySelector(".jdgm-rev__rating");
    const productLink = node.querySelector(".jdgm-rev__prod-link");
    const photoNode = node.querySelector(".jdgm-rev__pic-img");
    const rawPhoto = photoNode?.getAttribute("data-src") || photoNode?.getAttribute("src") || "";
    const photo = rawPhoto ? largePhotoUrl(new URL(rawPhoto, window.location.origin).href) : "";
    const timestamp = node.querySelector(".jdgm-rev__timestamp")?.getAttribute("data-content") || "";
    const productUrl = productLink?.getAttribute("href") || "/pages/reviews";
    const productTitle = clean(productLink?.textContent) || "NASHE";

    return {
      id: node.getAttribute("data-review-id") || photo || `${clean(node.querySelector(".jdgm-rev__author")?.textContent)}-${timestamp}`,
      author: clean(node.querySelector(".jdgm-rev__author")?.textContent) || "NASHE customer",
      initial: clean(node.querySelector(".jdgm-rev__icon")?.textContent || node.querySelector(".jdgm-rev__author")?.textContent?.[0]) || "N",
      rating: Number(ratingNode?.getAttribute("data-score")) || 5,
      body: clean(node.querySelector(".jdgm-rev__body")?.textContent) || "Photo review from a NASHE customer.",
      verified: node.getAttribute("data-verified-buyer") === "true" || Boolean(node.querySelector(".jdgm-rev__buyer-badge")),
      date: dateText(timestamp),
      productTitle,
      productUrl: productUrl.startsWith("http") ? productUrl : new URL(productUrl, window.location.origin).href,
      productImage: "",
      photo,
    };
  };

  const unescapeJudgeMeHtml = (html) => String(html || "")
    .replace(/\\u003c/g, "<")
    .replace(/\\u003e/g, ">")
    .replace(/\\u0027/g, "'")
    .replace(/\\"/g, '"')
    .replace(/\\\//g, "/")
    .replace(/\\u0026/g, "&");

  const readAttr = (html, pattern) => {
    const match = html.match(pattern);
    return match ? decodeHtml(match[1]) : "";
  };

  const normalizePhotoReviewHtml = (html) => {
    const photo = largePhotoUrl(readAttr(html, /(?:data-src|src|href)='(https:\/\/review-images\.judgeme\.com[^']+)'/i).replace(/&amp;/g, "&"));
    if (!photo) return null;
    const author = htmlText(readAttr(html, /<span class='jdgm-rev__author'[^>]*>([\s\S]*?)<\/span>/i));
    const initial = htmlText(readAttr(html, /<div class='jdgm-rev__icon'[^>]*>([\s\S]*?)<\/div>/i)) || author.slice(0, 1) || "N";
    const productTitle = htmlText(readAttr(html, /<a[^>]*class='jdgm-rev__prod-link'[^>]*>([\s\S]*?)<\/a>/i)) || "NASHE";
    const productUrl = readAttr(html, /<a href='([^']+)'[^>]*class='jdgm-rev__prod-link'/i) || "/pages/reviews";
    const body = htmlText(readAttr(html, /<div class='jdgm-rev__body'[^>]*>([\s\S]*?)<\/div>/i));

    return {
      id: readAttr(html, /data-review-id='([^']+)'/i) || photo,
      author: author || "NASHE customer",
      initial,
      rating: Number(readAttr(html, /class='jdgm-rev__rating' data-score='([^']+)'/i)) || 5,
      body: body || "Photo review from a NASHE customer.",
      verified: /data-verified-buyer='true'/i.test(html) || /jdgm-rev__buyer-badge/i.test(html),
      date: dateText(readAttr(html, /data-content='([^']+)'/i)),
      productTitle,
      productUrl: productUrl.startsWith("http") ? productUrl : new URL(productUrl, window.location.origin).href,
      productImage: "",
      photo,
    };
  };

  const photoReviewsFromRawHtml = (html) => {
    const normalized = unescapeJudgeMeHtml(html);
    return Array.from(normalized.matchAll(/<div class='jdgm-rev[\s\S]*?(?=<div class='jdgm-rev|$)/g))
      .map((match) => match[0])
      .filter((chunk) => /review-images\.judgeme\.com|jdgm-rev__pic-img/i.test(chunk))
      .map(normalizePhotoReviewHtml)
      .filter(Boolean);
  };

  const loadPhotoReviewsFromReviewsPage = async (root) => {
    const url = root.dataset.readMoreUrl || "/pages/reviews";
    const response = await fetch(url, { credentials: "omit", cache: "no-store" });
    if (!response.ok) return [];
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const domReviews = Array.from(doc.querySelectorAll(".jdgm-rev"))
      .map(normalizePhotoReviewNode)
      .filter((review) => review.photo);
    const rawReviews = photoReviewsFromRawHtml(html);
    return [...domReviews, ...rawReviews]
      .filter((review, index, all) => all.findIndex((item) => item.id === review.id || photoKey(item.photo) === photoKey(review.photo)) === index);
  };

  const card = (review) => {
    const media = review.photo
      ? `<div class="nashe-jr__media"><a class="nashe-jr__photo" href="${esc(review.photo)}" target="_blank" rel="nofollow noopener"><img src="${esc(review.photo)}" alt=""></a></div>`
      : "";
    const image = review.productImage
      ? `<span class="nashe-jr__product-image"><img src="${esc(review.productImage)}" alt=""></span>`
      : `<span class="nashe-jr__product-image" aria-hidden="true"></span>`;
    const productRowClass = review.productImage ? "nashe-jr__product-row" : "nashe-jr__product-row nashe-jr__product-row--no-image";

    return `
      <article class="nashe-jr__card${review.photo ? " nashe-jr__card--photo" : ""}">
        <div class="nashe-jr__card-main">
          <div class="nashe-jr__card-head">
            <span class="nashe-jr__avatar" aria-hidden="true">${esc(review.initial.slice(0, 1))}</span>
            <div>
              <div class="nashe-jr__author-row">
                <span class="nashe-jr__author">${esc(review.author)}</span>
                ${review.verified ? '<span class="nashe-jr__verified">Verified</span>' : ""}
              </div>
              ${review.date ? `<span class="nashe-jr__product-label">${esc(review.date)}</span>` : ""}
            </div>
          </div>
          <div class="nashe-jr__card-stars" aria-label="${esc(review.rating)} out of 5 stars">${esc(stars(review.rating))}</div>
          <p class="nashe-jr__text">${esc(review.body || "Srce zna odakle je.")}</p>
          ${media}
        </div>
        <div class="${productRowClass}">
          ${image}
          <span>
            <span class="nashe-jr__product-label">About</span>
            <a class="nashe-jr__product-title" href="${esc(review.productUrl)}">${esc(review.productTitle)}</a>
          </span>
        </div>
      </article>
    `;
  };

  const updateTabs = (root, mode) => {
    root.querySelectorAll("[data-nashe-jr-tab]").forEach((button) => {
      button.classList.toggle("is-active", button.dataset.nasheJrTab === mode);
    });
  };

  const render = (root, state, mode) => {
    const grid = root.querySelector("[data-nashe-jr-grid]");
    const filtered = mode === "photos" ? state.photoReviews : state.productReviews;
    root.classList.toggle("nashe-jr--photos", mode === "photos");
    updateTabs(root, mode);
    grid.innerHTML = filtered.length
      ? filtered.slice(0, 16).map(card).join("")
      : `<div class="nashe-jr__empty">${esc(root.dataset.emptyText)}</div>`;
  };

  const load = async (root) => {
    const token = root.dataset.apiToken;
    const shopDomain = root.dataset.shopDomain;
    if (!token || !shopDomain) throw new Error("Missing Judge.me public API settings");

    const base = "https://judge.me/api/v1/widgets/all_reviews_page";
    const urls = [1, 2].map((page) => {
      const params = new URLSearchParams({
        api_token: token,
        shop_domain: shopDomain,
        json_request: "true",
        per_page: "24",
        page: String(page),
      });
      return `${base}?${params}`;
    });

    const pages = await Promise.all(urls.map((url) => fetch(url).then((response) => {
      if (!response.ok) throw new Error(`Judge.me request failed: ${response.status}`);
      return response.json();
    })));

    const productReviews = pages
      .flatMap((page) => Array.isArray(page.reviews) ? page.reviews : [])
      .filter(isProductReview)
      .map(normalize)
      .filter((review) => review.body)
      .filter((review, index, all) => all.findIndex((item) => item.id === review.id) === index);

    let pagePhotoReviews = [];
    try {
      pagePhotoReviews = await loadPhotoReviewsFromReviewsPage(root);
    } catch {
      pagePhotoReviews = [];
    }

    const photoReviews = [...productReviews.filter((review) => review.photo), ...pagePhotoReviews]
      .filter((review, index, all) => all.findIndex((item) => item.id === review.id || photoKey(item.photo) === photoKey(review.photo)) === index);

    const state = { productReviews, photoReviews };

    const summary = root.querySelector("[data-nashe-jr-summary]");
    const avg = productReviews.length ? productReviews.reduce((sum, review) => sum + review.rating, 0) / productReviews.length : Number(root.dataset.storeRating) || 5;
    const count = Number(root.dataset.storeCount) || productReviews.length;
    summary.innerHTML = `
      <div class="nashe-jr__stars" aria-label="${avg.toFixed(1)} out of 5 stars">${esc(stars(avg))}</div>
      <div class="nashe-jr__summary-text">${avg.toFixed(1)} / 5 · ${count || productReviews.length} product reviews from NASHE people</div>
    `;

    root.querySelectorAll("[data-nashe-jr-tab]").forEach((button) => {
      button.addEventListener("click", () => render(root, state, button.dataset.nasheJrTab));
    });

    render(root, state, "product");
  };

  roots.forEach((root) => {
    load(root).catch(() => {
      const grid = root.querySelector("[data-nashe-jr-grid]");
      grid.innerHTML = `<div class="nashe-jr__empty">${esc(root.dataset.emptyText)}</div>`;
    });
  });
})();
